import { TopProfileMode } from '@project-gd-x/dating-site-contracts/src/gen/gdx/gateway/web/myprofile/v1/top_profile_pb';
import { Package } from '@project-gd-x/dating-site-contracts/src/gen/gdx/gateway/web/possibilities/v1/possibilities_pb';
import { computed, Ref } from 'vue';

import { customEventTrigger } from '@/helpers/gtm-triggers/gtm-triggers';
import { resolveProgressInPercent } from '@/helpers/number';
import {
  PossibilityPackage,
  PossibilityPackageWhitelistCleanup,
} from '@/pages/my-profile/components/my-profile-view/components/my-profile-boosters-grid/components/profile-card/helpers/package-id-helper/package-id-helper';
import { MyProfileStateShort } from '@/pages/my-profile/store/my-profile-state';
import { resolveCreditsLastAction } from '@/pages/payment/helpers/credits-last-action/credits-last-action';
import { PaymentStateBooster } from '@/pages/payment/store/payment-state';
import { purchasePossibility } from '@/services/api/possibilities/possibilities-api';
import { BOOSTER_TOP_PROFILE_ID, BOOSTER_TOP_PROFILE_PLUS_ID } from '@/settings';
import { CommonStateCreditsFeatures } from '@/store/modules/common/common-state';
import { dispatch, getState } from '@/store/store-helper';
import { ExtractClassFields } from '@/type';

const boosterMap: Record<string, string> = {
  [PossibilityPackage.TOP_PROFILE_COMMON]: BOOSTER_TOP_PROFILE_ID,
  [PossibilityPackage.TOP_PROFILE_PLUS]: BOOSTER_TOP_PROFILE_PLUS_ID,
};

export default function usePossibilities() {
  const creditFeatureMap = computed<CommonStateCreditsFeatures>(() => {
    return getState().common.creditsFeatures;
  });

  const packages = computed<ExtractClassFields<Package>[]>(() => {
    return getState().possibility.packages;
  });

  const shortProfile = computed<MyProfileStateShort>(() => {
    return getState().myProfile.short;
  });

  const topProfileModes = computed<Partial<TopProfileMode[]>>(() => {
    return shortProfile.value.topProfileMode;
  });

  const topProfileProgress = computed<number>(() => {
    const duration = shortProfile.value?.boosterEndsDurationMs / 1000;
    const expires = shortProfile.value?.boosterEndsTimeMs / 1000;

    return resolveProgressInPercent(duration, expires);
  });

  async function purchase(pkg: PossibilityPackage) {
    customEventTrigger({
      event: 'purchase-possibility',
      packageId: pkg,
    });

    await purchasePossibility(pkg);
  }

  async function activateTopProfile(
    type: PossibilityPackage,
    boosters: Ref<PaymentStateBooster[]>,
  ): Promise<void> {
    resolveCreditsLastAction(type);

    const booster = boosters.value?.find(
      (item: PaymentStateBooster) => item.id === boosterMap[type],
    );

    if (booster) {
      customEventTrigger({
        event: 'booster_activate',
        packageId: type,
      });
      await dispatch('actionPaymentActivateBooster', booster);
    }
  }

  async function requestData(isWhiteListCheck?: boolean): Promise<void> {
    const packageIds: PossibilityPackage[] = Object.values(
      isWhiteListCheck ? PossibilityPackageWhitelistCleanup : PossibilityPackage,
    );

    await Promise.all([
      dispatch('actionCommonGetFreeCreditsFeaturesStatus'),
      dispatch('actionPossibilityBatchGetPackages', packageIds),
    ]);
  }

  function init() {}

  return {
    creditFeatureMap,
    packages,
    topProfileModes,
    topProfileProgress,
    purchase,
    activateTopProfile,
    requestData,
    init,
  };
}
